import type { RateProduct, ProviderRate } from './rates';

/**
 * Mehrkosten math for the Zinsvergleich table — pure functions, unit-tested in
 * mehrkosten.test.ts.
 *
 * Mehrkosten p.a. = (rate − anchor rate) × volume, rounded to CHF 50.
 * Anchor = the Finavest top rate; when that is stale (null) the cheapest
 * visible provider anchors instead (its row then reads CHF 0).
 */

export const VOLUME_MIN = 100_000;
export const VOLUME_MAX = 5_000_000;
export const VOLUME_DEFAULT = 500_000;

export function clampVolume(n: number): number {
  return Math.min(VOLUME_MAX, Math.max(VOLUME_MIN, n));
}

/** Round to the nearest CHF 50 (… 25 → 50, 24.99 → 0, −75 → −100/−50 ties up). Never −0 ("CHF -0"). */
export function roundTo50(chf: number): number {
  return Math.round(chf / 50) * 50 || 0;
}

/** Annual extra cost of `ratePct` vs. `anchorPct` on `volumeChf`, CHF-50-rounded. */
export function mehrkosten(ratePct: number, anchorPct: number, volumeChf: number): number {
  return roundTo50(((ratePct - anchorPct) / 100) * volumeChf);
}

export interface ComparisonRow {
  name: string;
  ratePct: number;
  mehrkostenChf: number;
}

export interface Comparison {
  /** null when the Finavest rate is stale ("Top-Zins auf Anfrage" row). */
  finavestRatePct: number | null;
  /** Rate every row is measured against (Finavest, or cheapest provider on stale). */
  anchorPct: number;
  /** Providers sorted by rate ascending, Mehrkosten vs. the anchor. */
  rows: ComparisonRow[];
}

export function buildComparison(product: RateProduct, volumeChf: number): Comparison {
  const volume = clampVolume(volumeChf);
  const sorted: ProviderRate[] = [...product.providers].sort((a, b) => a.ratePct - b.ratePct);
  // sanitizeRates drops provider-less products, so sorted[0] exists; the 0
  // fallback only satisfies noUncheckedIndexedAccess.
  const anchorPct = product.finavestRatePct ?? sorted[0]?.ratePct ?? 0;
  return {
    finavestRatePct: product.finavestRatePct,
    anchorPct,
    rows: sorted.map((p) => ({
      name: p.name,
      ratePct: p.ratePct,
      mehrkostenChf: mehrkosten(p.ratePct, anchorPct, volume),
    })),
  };
}
